/**
 * HintPauseOverlay Component
 * 
 * Displays a coaching hint while pausing the session to give user time to read and process.
 * Features:
 * - Semi-transparent overlay
 * - Hint displayed prominently in center
 * - "Continue" button for immediate resume
 * - 5-second auto-resume timer for hands-free operation
 * - Visual progress bar showing time remaining
 */

import { useState, useEffect, useRef } from 'react';
import { LightBulbIcon, ExclamationTriangleIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

/**
 * Convert basic markdown to HTML for hint rendering
 * Supports: **bold**, line breaks
 */
function formatHintText(text: string): string {
  return text
    // Convert **bold** to <strong>
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold">$1</strong>')
    // Convert line breaks to <br>
    .replace(/\n/g, '<br/>');
}

interface HintPauseOverlayProps {
  hint: string;
  type: 'suggestion' | 'warning' | 'praise' | 'detailed_help';
  onContinue: () => void;
  autoResumeSeconds?: number; // Optional override, otherwise uses defaults
  isPaused?: boolean; // If true, countdown pauses
  onPauseMicrophone?: () => void; // NEW: Called when overlay appears to pause mic
  onResumeMicrophone?: () => void; // NEW: Called when overlay closes to resume mic
}

export default function HintPauseOverlay({ 
  hint, 
  type, 
  onContinue,
  autoResumeSeconds,
  isPaused = false,
  onPauseMicrophone,
  onResumeMicrophone
}: HintPauseOverlayProps) {
  const { t } = useTranslation();
  
  // Detailed help NEVER auto-closes (user must read and process)
  // Warnings/suggestions auto-close for flow
  const shouldAutoClose = type !== 'detailed_help';
  
  // Default timings based on type (only used if shouldAutoClose)
  const defaultSeconds = autoResumeSeconds || (
    type === 'warning' ? 3 : // Warnings: 3 seconds (urgent, quick)
    5 // Suggestions: 5 seconds (default)
  );
  
  const [timeRemaining, setTimeRemaining] = useState(defaultSeconds);
  const [progress, setProgress] = useState(100);
  
  // Use ref to track pause state without causing re-render
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;

  // Use ref to track onContinue callback to avoid dependency issues
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;

  // NEW: Pause microphone when overlay appears
  useEffect(() => {
    logger.debug('[HintPauseOverlay] Component mounted - pausing microphone');
    onPauseMicrophone?.();
    
    // Resume microphone when overlay unmounts
    return () => {
      logger.debug('[HintPauseOverlay] Component unmounting - resuming microphone');
      onResumeMicrophone?.();
    };
  }, [onPauseMicrophone, onResumeMicrophone]);

  useEffect(() => {
    // Detailed help doesn't auto-close
    if (!shouldAutoClose) {
      logger.debug('[HintPauseOverlay] Detailed help - NO auto-close timer');
      return;
    }

    // Don't start timer if paused
    if (isPausedRef.current) {
      logger.debug('[HintPauseOverlay] Starting in PAUSED state - will wait');
      return;
    }

    // Reset states when component mounts or hint changes
    setTimeRemaining(defaultSeconds);
    setProgress(100);

    // Countdown timer
    const startTime = Date.now();
    let pausedTime = 0;
    let lastPauseStart = 0;
    const duration = defaultSeconds * 1000;

    logger.debug('[HintPauseOverlay] Starting auto-close timer:', { defaultSeconds, type, hintLength: hint.length });

    const interval = setInterval(() => {
      // Check if paused
      if (isPausedRef.current) {
        if (lastPauseStart === 0) {
          lastPauseStart = Date.now();
        }
        return; // Don't update timer while paused
      } else if (lastPauseStart > 0) {
        // Was paused, now resumed - add pause duration
        pausedTime += Date.now() - lastPauseStart;
        lastPauseStart = 0;
      }
      
      const elapsed = Date.now() - startTime - pausedTime;
      const remaining = Math.max(0, defaultSeconds - Math.floor(elapsed / 1000));
      const progressPercent = Math.max(0, 100 - (elapsed / duration) * 100);
      
      setTimeRemaining(remaining);
      setProgress(progressPercent);

      if (elapsed >= duration) {
        logger.debug('[HintPauseOverlay] Timer completed, calling onContinue');
        clearInterval(interval);
        onContinueRef.current();
      }
    }, 100);

    return () => {
      logger.debug('[HintPauseOverlay] Cleanup timer');
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hint, type, defaultSeconds, shouldAutoClose]); // isPaused removed from dependencies

  // Type-specific styling
  const getTypeConfig = () => {
    switch (type) {
      case 'warning':
        return {
          icon: ExclamationTriangleIcon,
          bgGradient: 'from-red-50 to-orange-50',
          borderColor: 'border-red-400',
          iconBg: 'bg-red-500',
          label: t('scenario.session.hints.warning'),
          progressColor: 'bg-red-500'
        };
      case 'praise':
        return {
          icon: SparklesIcon,
          bgGradient: 'from-green-50 to-emerald-50',
          borderColor: 'border-green-400',
          iconBg: 'bg-green-500',
          label: t('scenario.session.hints.wellDone'),
          progressColor: 'bg-green-500'
        };
      case 'detailed_help':
        return {
          icon: LightBulbIcon,
          bgGradient: 'from-blue-50 to-indigo-50',
          borderColor: 'border-blue-400',
          iconBg: 'bg-blue-500',
          label: t('scenario.session.hints.strategicHelp'),
          progressColor: 'bg-blue-500'
        };
      default: // suggestion
        return {
          icon: LightBulbIcon,
          bgGradient: 'from-amber-50 to-yellow-50',
          borderColor: 'border-amber-400',
          iconBg: 'bg-amber-500',
          label: t('scenario.session.hints.coachingHint'),
          progressColor: 'bg-amber-500'
        };
    }
  };

  const config = getTypeConfig();
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-in fade-in duration-300 pointer-events-none">
      {/* Semi-transparent backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto" />
      
      {/* Hint card */}
      <div 
        className={`relative max-w-2xl w-full bg-gradient-to-br ${config.bgGradient} border-3 ${config.borderColor} rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 pointer-events-auto`}
      >
        {/* Progress bar at top - only for auto-closing hints */}
        {shouldAutoClose && (
          <div className="absolute top-0 left-0 right-0 h-2 bg-gray-200 rounded-t-2xl overflow-hidden">
            <div 
              className={`h-full ${config.progressColor} transition-all duration-100 ease-linear`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Content */}
        <div className="p-8 pt-10">
          {/* Icon and label */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 ${config.iconBg} rounded-full flex items-center justify-center animate-pulse`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{config.label}</div>
              <div className="text-sm text-gray-600">{t('scenario.session.hints.sessionPausedForReading')}</div>
            </div>
          </div>

          {/* Hint text - with scroll for long content */}
          <div className="bg-white/80 rounded-xl p-4 md:p-6 mb-6 border-2 border-gray-200 max-h-96 overflow-y-auto">
            <div 
              className="text-sm md:text-base text-gray-800 leading-loose"
              dangerouslySetInnerHTML={{ __html: formatHintText(hint) }}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4">
            {/* Auto-resume info - only for auto-closing hints */}
            {shouldAutoClose && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {t('scenario.session.hints.autoResumesIn')} <span className="font-bold">{timeRemaining}s</span>
                </span>
              </div>
            )}

            {/* Continue button */}
            <button
              onClick={() => {
                logger.debug('[HintPauseOverlay] Continue clicked - resuming microphone');
                // Resume microphone will happen automatically on unmount
                // But we call it explicitly here for immediate feedback
                onContinue();
              }}
              className={`px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 ${!shouldAutoClose ? 'w-full' : ''}`}
            >
              {shouldAutoClose ? t('scenario.session.hints.continueNow') + ' →' : t('scenario.session.hints.continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

